import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Star, HandshakeIcon, Car, ShieldCheck } from "lucide-react";
import { useSettings } from "../lib/settings";

// lucide doesn't export HandshakeIcon in older versions — fall back to a safe icon
const SafeHandshake = HandshakeIcon || Car;

export default function About() {
  const { settings } = useSettings();
  const s = settings;
  return (
    <div data-testid="page-about" className="pt-32 pb-24 max-w-[1500px] mx-auto px-6 md:px-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="overline">About Us</div>
        <h1 className="font-display text-6xl md:text-8xl mt-3 tracking-tighter">
          Your local
          <br />
          <span className="text-[var(--primary)]">used car dealer.</span>
        </h1>
      </motion.div>

      {/* Bento */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(200px,auto)]">
        <div className="bento-cell md:col-span-7 md:row-span-2 flex flex-col justify-between overflow-hidden relative">
          <img
            src="https://images.unsplash.com/photo-1574023278969-abb7ab49945c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Car lot"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="relative z-10">
            <div className="overline">Who We Are</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tighter">
              A used car lot
              <br />
              on Gallatin Pike.
            </h2>
          </div>
          <p className="relative z-10 text-white/70 max-w-md mt-8 leading-relaxed">
            Pick Auto Sales is a locally-owned used car dealership in Madison, TN, serving
            drivers across the greater Nashville area. Walk the lot, ask questions, take a
            test drive. If something isn&apos;t right, we&apos;ll tell you.
          </p>
        </div>

        <div className="bento-cell md:col-span-5 flex flex-col justify-between">
          <Star size={22} className="text-[var(--primary)]" />
          <div>
            <div className="font-display text-5xl tracking-tighter">{s.rating || "4.6★"}</div>
            <div className="overline mt-2">Google customer rating</div>
          </div>
        </div>

        <div className="bento-cell md:col-span-5 flex flex-col justify-between">
          <SafeHandshake size={22} className="text-[var(--primary)]" />
          <div>
            <h3 className="font-display text-3xl tracking-tighter">No-Pressure Visits</h3>
            <p className="text-white/60 text-sm mt-2">
              Test drive as many as you want. Bring a mechanic. Take your time. That&apos;s the deal.
            </p>
          </div>
        </div>

        <div className="bento-cell md:col-span-4 flex flex-col justify-between">
          <Car size={22} className="text-[var(--primary)]" />
          <div>
            <div className="font-display text-3xl tracking-tighter">Used Cars,<br/>Trucks & SUVs</div>
            <div className="overline mt-2">All makes considered</div>
          </div>
        </div>

        <div className="bento-cell md:col-span-8">
          <ShieldCheck size={22} className="text-[var(--primary)]" />
          <blockquote className="font-display text-3xl md:text-4xl mt-6 tracking-tighter leading-tight">
            Come by, look under the hood, take it around the block. We&apos;ll be
            straight with you about anything we know.
          </blockquote>
          <div className="overline mt-4">The Pick Auto promise</div>
        </div>
      </div>

      {/* Visit */}
      <div className="mt-24">
        <div className="overline">Visit Us</div>
        <h2 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">
          Come see the lot
          <br />
          in person.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          <InfoCard icon={MapPin} title="Where">
            {s.address_line1}<br />
            {s.address_line2}
          </InfoCard>
          <InfoCard icon={Phone} title="Call">{s.phone}</InfoCard>
          <InfoCard icon={Clock} title="Hours">
            {s.hours_weekday}<br />
            {s.hours_sunday}
          </InfoCard>
        </div>
      </div>
    </div>
  );
}

const InfoCard = ({ icon: Icon, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bento-cell"
  >
    <Icon size={22} className="text-[var(--primary)]" />
    <div className="overline mt-4">{title}</div>
    <div className="mt-2 text-white/80 leading-relaxed">{children}</div>
  </motion.div>
);
